import React, { useCallback, useEffect, useRef, useState } from "react";
import { ChevronDown } from "lucide-react";
import { useAppSelector } from "@/state/hooks";
import { NavigationBackButton } from "../NavigationBackButton";
import { ContextMeshApiError, type ContextMeshAnalysis, type ContextMeshCategoryKey } from "./contextMeshAnalysis";
import {
    fetchContextMeshReport,
    type ContextMeshReportItem,
    type ContextMeshReportPage
} from "./contextMeshReport";
import { StrengthWheel } from "./StrengthWheel";
import {
    CONTEXT_MESH_CATEGORIES,
    type ContextMeshCategoryTheme
} from "./contextMeshTheme";
import {
    sourceDiagnosticBadge,
    sourceDiagnosticEmptyState
} from "./contextMeshSourceDiagnostics";
import type { ContextMeshRefreshNotice } from "./useMeshStrengthData";

interface ContextMeshScoreReportProps {
    productId?: number;
    contextMeshId?: string;
    contextMeshTitle?: string | null;
    analysis: ContextMeshAnalysis;
    refreshing: boolean;
    refreshNotice: ContextMeshRefreshNotice | null;
    onRefresh: () => Promise<boolean>;
    onClose: () => void;
}

interface ReportTab extends Pick<ContextMeshCategoryTheme, "key" | "color" | "accentColor"> {
    label: string;
}

interface PageState {
    items: ContextMeshReportItem[];
    nextCursor: string | null;
    report: ContextMeshReportPage | null;
    loading: boolean;
    loaded: boolean;
    error: ContextMeshApiError | null;
}

const REPORT_TABS: ReportTab[] = CONTEXT_MESH_CATEGORIES.map(category => ({
    key: category.key,
    label: category.shortTitle,
    color: category.color,
    accentColor: category.accentColor
}));

const emptyPage = (): PageState => ({
    items: [],
    nextCursor: null,
    report: null,
    loading: false,
    loaded: false,
    error: null
});

function pageKey(category: ContextMeshCategoryKey, parent?: Pick<ContextMeshReportItem, "type" | "id">): string {
    return parent ? `${category}:${parent.type}:${parent.id}` : `${category}:root`;
}

function scoreColor(score: number | null): string {
    if (score === null) return "var(--fg4)";
    if (score >= 60) return "#0d9488";
    if (score >= 35) return "#d97706";
    return "#dc2626";
}

function scoreLabel(item: ContextMeshReportItem): string {
    if (item.status === "processing") return "Analyzing";
    if (item.status !== "rated" || item.score === null) return "Not rated";
    return String(item.score);
}

function relevanceStyle(item: ContextMeshReportItem): { label: string; color: string; background: string } {
    if (item.status === "processing") return { label: "ANALYZING", color: "#1d4ed8", background: "#eff6ff" };
    if (item.status === "failed") return { label: "FAILED", color: "#b91c1c", background: "#fef2f2" };
    if (item.status !== "rated") return { label: "NOT RATED", color: "var(--fg4)", background: "var(--bg-input)" };
    if (item.relevanceReason === "DUPLICATE_CONTENT") return { label: "DUPLICATE", color: "#6b21a8", background: "#faf5ff" };
    if (item.relevanceReason === "SELECTION_CAP") return { label: "NOT SELECTED", color: "#475569", background: "#f1f5f9" };
    if (item.relevanceReason === "LOW_DETAIL") return { label: "NEEDS DETAIL", color: "#b45309", background: "#fff7ed" };
    if (item.relevanceReason === "MISSING_CAPABILITY_COVERAGE") return { label: "NEEDS COVERAGE", color: "#b45309", background: "#fff7ed" };
    if (item.relevance === "relevant") return { label: "RELEVANT", color: "#0f766e", background: "rgba(13,148,136,.10)" };
    if (item.relevance === "low_relevance") return { label: "LOW RELEVANCE", color: "#b45309", background: "rgba(217,119,6,.10)" };
    return { label: "NOT RATED", color: "var(--fg4)", background: "var(--bg-input)" };
}

function recommendationText(item: ContextMeshReportItem): string {
    if (item.recommendation) return item.recommendation.message;
    if (item.status === "processing") return "A recommendation will appear when analysis completes.";
    if (item.status === "failed" || item.status === "not_rated") return "A recommendation is not available until this item can be rated.";
    if (item.relevance === "relevant") return "No action needed.";
    return "No recommendation returned.";
}

function relevanceDetail(item: ContextMeshReportItem): string {
    const reason = item.relevanceReason.trim();
    // Current backends also carry the stable machine reason code in this
    // field. The current-state copy already explains that code for people.
    return reason && !/^[A-Z0-9_]+$/.test(reason) ? ` ${reason}` : "";
}

function childSummary(item: ContextMeshReportItem): string {
    if (item.evidenceCounts) {
        return `${item.evidenceCounts.selected} selected of ${item.evidenceCounts.analyzed} analyzed`;
    }
    return `${item.childCount} ${item.childCount === 1 ? "item" : "items"}`;
}

function ReportRow({ label, children }: { label: string; children: React.ReactNode }) {
    return (
        <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 7, paddingTop: 7, borderTop: "1px solid var(--divider)", minWidth: 0 }}>
            <span style={{ flex: "0 0 auto", fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em", color: "var(--fg4)" }}>{label}</span>
            <span style={{ minWidth: 0, flex: "1 1 auto", fontSize: 12, color: "var(--fg3)", lineHeight: 1.5 }}>{children}</span>
        </div>
    );
}

export function ContextMeshScoreReport({
    productId,
    contextMeshId,
    contextMeshTitle = null,
    analysis,
    refreshing,
    refreshNotice,
    onRefresh,
    onClose
}: ContextMeshScoreReportProps) {
    const token = useAppSelector(state => state.clientToken.token);
    const [activeCategory, setActiveCategory] = useState<ContextMeshCategoryKey>("discover");
    const [pages, setPages] = useState<Record<string, PageState>>({});
    const pagesRef = useRef(pages);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());
    const controllersRef = useRef(new Map<string, AbortController>());

    const clearReport = useCallback(() => {
        for (const controller of controllersRef.current.values()) controller.abort();
        controllersRef.current.clear();
        pagesRef.current = {};
        setPages({});
        setExpanded(new Set());
    }, []);

    const updatePage = useCallback((key: string, update: (current: PageState) => PageState) => {
        const next = { ...pagesRef.current, [key]: update(pagesRef.current[key] ?? emptyPage()) };
        pagesRef.current = next;
        setPages(next);
    }, []);

    const loadPage = useCallback(async (
        category: ContextMeshCategoryKey,
        parent?: Pick<ContextMeshReportItem, "type" | "id">,
        append = false
    ) => {
        if (!token) return;
        const key = pageKey(category, parent);
        const current = pagesRef.current[key] ?? emptyPage();
        if (current.loading || (append && !current.nextCursor)) return;
        controllersRef.current.get(key)?.abort();
        const controller = new AbortController();
        controllersRef.current.set(key, controller);
        updatePage(key, state => ({ ...state, loading: true, error: null }));
        try {
            const target = productId != null ? productId : { contextMeshId: contextMeshId! };
            const report = await fetchContextMeshReport(token, target, {
                category,
                ...(parent ? { parentType: parent.type, parentId: parent.id } : {}),
                ...(append && current.nextCursor ? { cursor: current.nextCursor } : {}),
                limit: 50
            }, controller.signal);
            const rootReport = pagesRef.current[pageKey(category)]?.report;
            if (parent && rootReport && rootReport.runId !== report.runId) {
                throw new ContextMeshApiError(409, { code: "REPORT_REFRESHED", message: "The analysis changed while this report was open. Reload this tab to see the latest results." });
            }
            updatePage(key, state => ({
                items: append ? [...state.items, ...report.items] : report.items,
                nextCursor: report.nextCursor,
                report,
                loading: false,
                loaded: true,
                error: null
            }));
        } catch (reason) {
            if (reason instanceof DOMException && reason.name === "AbortError") return;
            updatePage(key, state => ({
                ...state,
                loading: false,
                loaded: true,
                error: reason instanceof ContextMeshApiError
                    ? reason
                    : new ContextMeshApiError(503, { code: "REPORT_UNAVAILABLE", message: "The score detail report is temporarily unavailable." })
            }));
        } finally {
            if (controllersRef.current.get(key) === controller) controllersRef.current.delete(key);
        }
    }, [contextMeshId, productId, token, updatePage]);

    useEffect(() => {
        clearReport();
    }, [clearReport, contextMeshId, productId]);

    useEffect(() => {
        const root = pagesRef.current[pageKey(activeCategory)];
        if (token && !root?.loaded && !root?.loading) void loadPage(activeCategory);
    }, [activeCategory, loadPage, token]);

    useEffect(() => {
        const onKeyDown = (event: KeyboardEvent) => {
            if (event.key === "Escape") onClose();
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [onClose]);

    useEffect(() => () => {
        for (const controller of controllersRef.current.values()) controller.abort();
    }, []);

    const toggleItem = (item: ContextMeshReportItem) => {
        const key = pageKey(activeCategory, item);
        const opening = !expanded.has(key);
        setExpanded(current => {
            const next = new Set(current);
            if (opening) next.add(key);
            else next.delete(key);
            return next;
        });
        if (opening && !pagesRef.current[key]?.loaded) void loadPage(activeCategory, item);
    };

    const refreshReport = async () => {
        if (!await onRefresh()) return;
        clearReport();
        await loadPage(activeCategory);
    };

    const renderItems = (items: ContextMeshReportItem[], depth = 0): React.ReactNode => items.map(item => {
        const key = pageKey(activeCategory, item);
        const childPage = pages[key] ?? emptyPage();
        const isExpanded = expanded.has(key);
        const relevance = relevanceStyle(item);
        return (
            <React.Fragment key={`${item.type}:${item.id}`}>
                <div
                    data-testid={`report-item-${item.type}-${item.id}`}
                    style={{
                        border: "1px solid var(--border, rgba(15,23,42,.08))",
                        borderRadius: 10,
                        padding: "13px 14px",
                        marginLeft: Math.min(depth, 4) * 16,
                        background: "var(--bg-paper)",
                        boxShadow: "0 1px 2px rgba(15,23,42,.04)"
                    }}
                >
                    <div style={{ display: "flex", alignItems: "center", gap: 10, minWidth: 0 }}>
                        <span style={{ fontSize: 13.5, fontWeight: 700, flex: 1, minWidth: 0, overflowWrap: "anywhere", color: "var(--fg1)" }}>
                            {item.name}
                            {item.path && item.path !== item.name && <span style={{ marginLeft: 7, fontSize: 10.5, fontWeight: 500, color: "var(--fg4)" }}>{item.path}</span>}
                            {item.hasChildren && <span style={{ marginLeft: 7, fontSize: 10.5, fontWeight: 500, color: "var(--fg4)" }}>{childSummary(item)}</span>}
                        </span>
                        {item.type !== "external_rating" && <span style={{ fontFamily: "var(--font-mono)", fontWeight: 700, fontSize: item.score === null ? 12 : 16, lineHeight: 1, color: scoreColor(item.status === "rated" ? item.score : null), flex: "0 0 auto" }}>{scoreLabel(item)}</span>}
                        <span style={{ fontSize: 10, fontWeight: 700, letterSpacing: ".05em", color: relevance.color, background: relevance.background, padding: "2px 8px", borderRadius: 999, flex: "0 0 auto" }}>{relevance.label}</span>
                        {item.hasChildren && (
                            <button
                                type="button"
                                data-testid={`report-disclosure-${item.type}-${item.id}`}
                                aria-expanded={isExpanded}
                                aria-label={`${isExpanded ? "Collapse" : "Expand"} ${item.name}`}
                                onClick={() => toggleItem(item)}
                                style={{ width: 30, height: 30, marginLeft: 2, padding: 0, border: 0, borderRadius: 7, background: "transparent", color: "var(--fg4)", cursor: "pointer", display: "inline-flex", alignItems: "center", justifyContent: "center", flex: "0 0 auto" }}
                            >
                                <ChevronDown
                                    aria-hidden
                                    size={16}
                                    strokeWidth={1.8}
                                    style={{ transform: isExpanded ? "rotate(180deg)" : "none", transition: "transform .15s ease" }}
                                />
                            </button>
                        )}
                    </div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, minWidth: 0, marginTop: 8 }}>
                        <span style={{ flex: "0 0 auto", fontSize: 9.5, fontWeight: 700, letterSpacing: ".06em", color: "var(--fg4)" }}>CURRENT STATE</span>
                        <span style={{ minWidth: 0, flex: "1 1 auto", fontSize: 12, color: "var(--fg3)", lineHeight: 1.5 }}>
                            {item.currentState}{relevanceDetail(item)}
                        </span>
                    </div>
                    <ReportRow label="SCORE METHOD">{item.method}</ReportRow>
                    <ReportRow label="RECOMMENDATION">{recommendationText(item)}</ReportRow>
                </div>
                {isExpanded && (
                    <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 9 }}>
                        {childPage.loading && childPage.items.length === 0 && <div role="status" style={{ marginLeft: (depth + 1) * 18, padding: "12px 15px", color: "var(--fg3)", fontSize: 12 }}>Loading {item.name}…</div>}
                        {childPage.error && (
                            <div role="alert" style={{ marginLeft: (depth + 1) * 18, padding: "12px 15px", border: "1px solid #fecaca", borderRadius: 10, color: "#b91c1c", fontSize: 12 }}>
                                {childPage.error.message}{" "}
                                <button type="button" onClick={() => void loadPage(activeCategory, item)} style={{ padding: 0, border: 0, background: "none", color: "inherit", fontWeight: 700, cursor: "pointer" }}>Retry</button>
                            </div>
                        )}
                        {!childPage.loading && !childPage.error && childPage.loaded && childPage.items.length === 0 && <div style={{ marginLeft: (depth + 1) * 18, padding: "12px 15px", color: "var(--fg4)", fontSize: 12 }}>No child items were evaluated.</div>}
                        {renderItems(childPage.items, depth + 1)}
                        {childPage.nextCursor && (
                            <button type="button" disabled={childPage.loading} onClick={() => void loadPage(activeCategory, item, true)} style={{ alignSelf: "center", minHeight: 32, padding: "0 14px", borderRadius: 8, border: "1px solid var(--divider)", background: "var(--bg-paper)", color: "var(--fg2)", fontFamily: "inherit", fontWeight: 700, cursor: childPage.loading ? "wait" : "pointer" }}>
                                {childPage.loading ? "Loading…" : "Load more"}
                            </button>
                        )}
                    </div>
                )}
            </React.Fragment>
        );
    });

    const root = pages[pageKey(activeCategory)] ?? emptyPage();
    const activeTab = REPORT_TABS.find(tab => tab.key === activeCategory)!;
    const sourceDiagnostic = activeCategory === "discover" ? null : root.report?.sourceDiagnostic ?? null;
    const emptyState = sourceDiagnostic && activeCategory !== "discover"
        ? sourceDiagnosticEmptyState(activeCategory, sourceDiagnostic)
        : "No items evaluated for this context yet.";

    return (
        <section className="np-v6" aria-label="Score Detail Report" data-testid="score-detail-report" style={{ height: "100%", display: "flex", flexDirection: "column", background: "#f7f7f9", overflow: "hidden" }}>
            <div data-testid="score-detail-header" style={{ minHeight: 76, padding: "14px 28px", display: "flex", alignItems: "center", gap: 16, borderBottom: "1px solid var(--divider)", background: "var(--bg-paper)", flex: "0 0 auto", flexWrap: "wrap" }}>
                <NavigationBackButton label="Back to Context Mesh" onClick={onClose} description="Close the score detail report" bridgeId="btn.close-score-detail-report" />
                <div style={{ flex: "1 1 360px", minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 12, minWidth: 0 }}>
                        <span style={{ fontSize: 22, fontWeight: 650, letterSpacing: "-0.012em", color: "var(--fg1)", whiteSpace: "nowrap" }}>Score Detail Report</span>
                        {contextMeshTitle && (
                            <>
                                <span aria-hidden="true" style={{ fontSize: 20, fontWeight: 300, color: "var(--fg4)" }}>|</span>
                                <span title={contextMeshTitle} style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", fontSize: 14, fontWeight: 500, color: "var(--fg1)", whiteSpace: "nowrap" }}>
                                    {contextMeshTitle}
                                </span>
                            </>
                        )}
                    </div>
                    <div style={{ fontSize: 13, color: "var(--fg3)", marginTop: 3 }}>See how each source contributes to this product’s Context Mesh score.</div>
                </div>
                {refreshNotice && (
                    <span
                        role={refreshNotice.kind === "error" ? "alert" : "status"}
                        style={{ maxWidth: 260, fontSize: 11.5, color: refreshNotice.kind === "error" ? "#b91c1c" : "#047857" }}
                    >
                        {refreshNotice.message}
                    </span>
                )}
                <button
                    type="button"
                    data-ui-bridge="mesh-strength.refresh-score"
                    data-ui-label="Refresh score"
                    data-ui-description="Refresh the current Context Mesh score"
                    disabled={refreshing}
                    onClick={() => void refreshReport()}
                    style={{ minHeight: 34, padding: "0 14px", borderRadius: 9, border: "1px solid var(--np-brand, #2733C2)", background: "var(--bg-paper)", color: "var(--np-brand, #2733C2)", fontFamily: "inherit", fontSize: 12, fontWeight: 700, cursor: refreshing ? "wait" : "pointer", opacity: refreshing ? 0.65 : 1, whiteSpace: "nowrap" }}
                >
                    {refreshing ? "Refreshing…" : "Refresh score"}
                </button>
            </div>
            <div data-testid="score-detail-content" style={{ flex: 1, minHeight: 0, overflowY: "auto", padding: "20px 28px 28px", background: "#f7f7f9" }}>
                <div style={{ maxWidth: 1180, margin: "0 auto", display: "flex", gap: 24, alignItems: "flex-start", flexWrap: "wrap" }}>
                    <div style={{ flex: "0 1 300px", minWidth: 240, display: "flex", flexDirection: "column", gap: 14 }}>
                        <div>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "var(--fg1)", marginBottom: 5 }}>Report Description</div>
                            <div style={{ fontSize: 12, lineHeight: 1.55, color: "var(--fg3)" }}>This report breaks the Context Mesh score down by source. Select a tab to see which context was evaluated, how it supports the product, and where action may be needed.</div>
                        </div>
                        <div style={{ width: "100%", maxWidth: 280, aspectRatio: "1 / 1", margin: "4px auto 0" }}>
                            <StrengthWheel score={analysis.score} tier={analysis.tier} categoryScores={analysis.categoryScores} active={activeCategory} uid="score-report" />
                        </div>
                    </div>
                    <div style={{ flex: "1 1 560px", minWidth: 0 }}>
                        <div role="tablist" aria-label="Context score categories" style={{ display: "flex", gap: 6, borderBottom: "1px solid var(--divider)", marginBottom: 14, overflowX: "auto" }}>
                            {REPORT_TABS.map(tab => (
                                <button
                                    key={tab.key}
                                    type="button"
                                    role="tab"
                                    aria-selected={activeCategory === tab.key}
                                    onClick={() => setActiveCategory(tab.key)}
                                    style={{ padding: "9px 14px", border: 0, borderBottom: `2px solid ${activeCategory === tab.key ? tab.color : "transparent"}`, background: "transparent", color: activeCategory === tab.key ? tab.accentColor : "var(--fg3)", fontFamily: "inherit", fontSize: 12.5, fontWeight: 700, cursor: "pointer", whiteSpace: "nowrap" }}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>
                        {root.report && (
                            <div style={{ marginBottom: 12, padding: "9px 12px", borderRadius: 9, background: `color-mix(in srgb, ${activeTab.color} 7%, var(--bg-paper))`, color: "var(--fg3)", fontSize: 12, lineHeight: 1.45 }}>
                                <strong style={{ color: "var(--fg2)" }}>Score method:</strong> {root.report.method}
                            </div>
                        )}
                        {sourceDiagnostic && activeCategory !== "discover" && (
                            <div
                                data-testid={`report-source-diagnostic-${activeCategory}`}
                                style={{ marginBottom: 12 }}
                            >
                                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap" }}>
                                    <span
                                        style={{
                                            padding: "2px 7px",
                                            borderRadius: 999,
                                            background: "#eff6ff",
                                            color: "#1d4ed8",
                                            fontSize: 10,
                                            fontWeight: 700
                                        }}
                                    >
                                        {sourceDiagnosticBadge(activeCategory, sourceDiagnostic)}
                                    </span>
                                </div>
                            </div>
                        )}
                        {(!token || root.loading) && root.items.length === 0 && <div role="status" data-testid="score-report-loading" style={{ padding: "44px 16px", textAlign: "center", color: "var(--fg3)", fontSize: 13 }}>Loading {activeTab.label} score details…</div>}
                        {root.error && (
                            <div role="alert" style={{ padding: "18px", border: "1px solid #fecaca", borderRadius: 12, background: "#fef2f2", color: "#b91c1c", textAlign: "center" }}>
                                <div style={{ fontWeight: 700 }}>Score details are unavailable</div>
                                <div style={{ fontSize: 12, lineHeight: 1.5, margin: "5px 0 10px" }}>{root.error.message}</div>
                                <button type="button" onClick={() => void loadPage(activeCategory)} style={{ minHeight: 32, padding: "0 14px", borderRadius: 8, border: "1px solid currentColor", background: "transparent", color: "inherit", fontFamily: "inherit", fontWeight: 700, cursor: "pointer" }}>Retry</button>
                            </div>
                        )}
                        {!root.loading && !root.error && root.loaded && root.items.length === 0 && (
                            <div data-testid="score-report-empty" style={{ padding: "44px 16px", textAlign: "center", color: "var(--fg3)", fontSize: 13 }}>{emptyState}</div>
                        )}
                        <div role="tabpanel" aria-label={`${activeTab.label} score details`} style={{ display: "flex", flexDirection: "column", gap: 9 }}>
                            {renderItems(root.items)}
                            {root.nextCursor && (
                                <button type="button" disabled={root.loading} onClick={() => void loadPage(activeCategory, undefined, true)} style={{ alignSelf: "center", minHeight: 34, padding: "0 16px", borderRadius: 9, border: "1px solid var(--divider)", background: "var(--bg-paper)", color: "var(--fg2)", fontFamily: "inherit", fontWeight: 700, cursor: root.loading ? "wait" : "pointer" }}>
                                    {root.loading ? "Loading…" : "Load more"}
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}
